import * as React from "react";
import { Stack, MessageBar, MessageBarType, ActionButton, Spinner, CompoundButton, SpinnerSize } from "@fluentui/react";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { RootState } from "../../redux";
import { useSelector } from "react-redux";
import { logger } from "../../../../utilities/logger";
import { IFileSaveResult } from "../_v2/redux/api/local/api-local-interfaces";
import authModule from "../../../../utilities/AuthModule";
import { getItemAbsoluteUrl } from "../../services/SharePointRestServices";
import { ComponentTypes } from "../../config/config";
import { SearchOpenExtensionHelper } from "../helpers/SearchOpenExtensionHelper";
import { getFileItemInfo, getItemCustomSelect, getItemWebViewUrl, getListItemWebUrl } from "../../services/GraphService";
import { GetOfficeContextApp } from "../helpers/officeHelper";
import { SaveMode } from "../enums/SaveModeEnum";
import { IDriveInfo } from "../../interfaces/IDriveInfo";
import generalModule from "../../../../utilities/GeneralModule";
import { OneDriveUrlHelper } from "../helpers/oneDriveUrlHelper";

interface IAfterSaveAppSelectorProps {
  onSaveLocation?: () => void;
  saveResult?: IFileSaveResult;
  saveMode: SaveMode
}

const getFileExtension = (name: string) => {
  const fileParts = name?.split(".");
  return fileParts?.length > 1 ? fileParts[fileParts.length - 1] : "";
}

export const AfterSaveAppSelector = ({ onSaveLocation, saveResult, saveMode }: IAfterSaveAppSelectorProps) => {
  const languageStrings = React.useContext(LanguageContext);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories?.workingDirectories[state.workingDirectories?.workingDirectories?.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);
  const oneDriveWebUrl = useSelector((state: RootState) => state.user.oneDriveWebUrl);
  const userAccount = useSelector((state: RootState) => state.user.account);

  const [isOpeningOnline, setIsOpeningOnline] = React.useState(false);
  const [isOpeningLocal, setIsOpeningLocal] = React.useState(false);

  const isInOneDrive = React.useRef(currentDirectory?.type == ComponentTypes.OneDrive || currentDirectory?.type == ComponentTypes.OneDriveSubFolder || currentDirectory?.webUrl === oneDriveWebUrl);

  const getDriveInfo = (): IDriveInfo => {
    return !currentDirectory.parentId
      ? { id: currentDirectory.id, driveId: null }
      : { id: currentDirectory.id, driveId: currentDirectory.parentId };
  };

  const openFile = async (isLocal: boolean) => {
    try {
      isLocal ? setIsOpeningLocal(true) : setIsOpeningOnline(true);

      if (saveResult && saveResult.succeededFiles?.length > 0) {
        const uploadedFile = saveResult.succeededFiles[0];

        if (uploadedFile) {
          if (isLocal && saveMode === SaveMode.SaveAsPdf && getFileExtension(uploadedFile.name)?.trim().toLowerCase() === "pdf") {
            const driveId = !isInOneDrive.current ? (!currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId) : oneDriveRootDetails.driveId;

            const userEmail: string = userAccount.username;
            const userId: string = userAccount.uniqueId;

            let file: any;
            let itemId: string;
            let fileName: string;
            let fileId: string;
            let siteId: string;
            let listId: string;
            let webUrl: string;

            if (isInOneDrive.current) {
              itemId = uploadedFile.uniqueId;
              file = itemId && await getItemCustomSelect(await authModule.getGraphAccessToken(), driveId, itemId, "name,sharepointIds");
            }
            else {
              file = await getFileItemInfo(getDriveInfo(), uploadedFile.name, "id,name,sharepointIds");
            }

            fileId = file.sharepointIds.listItemUniqueId;
            siteId = file.sharepointIds.siteId;
            listId = file.sharepointIds.listId;
            webUrl = file.sharepointIds.siteUrl;

            window.open(OneDriveUrlHelper.getOpenInOneDriveDesktopUrl(fileId, siteId, listId, userEmail, userId, webUrl, fileName), generalModule.isInConentMangerMode() ? "_self" : "_blank");
          }
          else {
            let url: string;
            if (isLocal) {
              url = isInOneDrive.current ? await getListItemWebUrl(await authModule.getGraphAccessToken(), oneDriveRootDetails.driveId, uploadedFile.uniqueId) : await getItemAbsoluteUrl(await authModule.getSharePointAccessToken(), currentDirectory.webUrl, currentDirectory.sharePointListId, uploadedFile.uniqueId);
            } else {
              const driveId = !isInOneDrive.current ? (!currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId) : oneDriveRootDetails.driveId;
              url = await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, uploadedFile.uniqueId);
            }

            if (url) {
              if (isLocal) {
                const fileInfo = SearchOpenExtensionHelper.determineOfficeFileType(getFileExtension(uploadedFile.name));
                if (fileInfo?.localUrlPrepended) {
                  window.open(`${fileInfo.localUrlPrepended}${url}`);
                }
              } else {
                window.open(url);
              }
            }
          }
        }
      }

      setIsOpeningLocal(false);
      setIsOpeningOnline(false);

    } catch (error) {
      logger(error, "AfterSaveAppSelector.tsx (openFile)");
    }
  }

  return (
    <Stack tokens={{ childrenGap: 15 }}>
      {saveMode === SaveMode.Default && <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
        {languageStrings.NotWorkingOnOnlineVersion}
      </MessageBar>}
      <CompoundButton primary text={saveMode === SaveMode.Default ? `${languageStrings.OpenIn} ${GetOfficeContextApp().name}` : `${languageStrings.OpenIn} ${languageStrings.App}`} secondaryText={`${languageStrings.OpenOnlineDocumentWith} app`} onClick={async () => openFile(true)}>
        {isOpeningLocal && <Spinner size={SpinnerSize.large} />}
      </CompoundButton>
      <CompoundButton text={saveMode === SaveMode.Default ? `${languageStrings.OpenIn} ${GetOfficeContextApp().name} Online` : `${languageStrings.OpenIn} ${languageStrings.Browser}`} secondaryText={`${languageStrings.OpenOnlineDocumentWith} browser`} onClick={async () => openFile(false)}>
        {isOpeningOnline && <Spinner size={SpinnerSize.large} />}
      </CompoundButton>
      <ActionButton
        iconProps={{ iconName: "NavigateBackMirrored" }}
        allowDisabledFocus
        onClick={() => {
          onSaveLocation()
        }}
        text={languageStrings.Continue}
      />
    </Stack>
  );
};
